import { Alert, Box, Divider, InputLabel, Typography } from '@mui/material'
import { FieldProps } from '@rjsf/utils/dist'
import { useConfiguratorModelTreeStructureLazyQuery } from 'apollo/package_manager/queries/ConfiguratorModelTreeStructure.generated'
import { ModelContext } from 'context/model/ModelContext'
import { useContext, useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import CheckboxTree, { Node } from 'react-checkbox-tree'
import 'react-checkbox-tree/lib/react-checkbox-tree.css'
import { formatPath } from './utils'

const DependencyPathSelector = (props: FieldProps) => {
    const translate = useTranslate()
    const { state: modelState } = useContext(ModelContext)
    const [checked, setChecked] = useState<string[]>([props.value ?? '$'])
    const [expanded, setExpanded] = useState<string[]>([])
    const [parsedConfiguratorModelTreeStructure, setParsedConfiguratorModelTreeStructure] =
        useState<Node[] | null>([])

    /*
     * Query for getting the model tree structure
     */
    const [getConfiguratorModelTreeStructure, { loading: isTreeStructureLoading }] =
        useConfiguratorModelTreeStructureLazyQuery({
            onCompleted: (data) => {
                if (data?.configuratorModelTreeStructure)
                    setParsedConfiguratorModelTreeStructure(
                        JSON.parse(data.configuratorModelTreeStructure)
                    )
            },
            onError: () => {
                setParsedConfiguratorModelTreeStructure([])
            }
        })

    /*
     * UseEffect to load tree structure when component mounts or model changes
     */
    useEffect(() => {
        if (modelState.currentModelCode) {
            getConfiguratorModelTreeStructure({
                variables: { code: modelState.currentModelCode }
            })
        }
    }, [modelState.currentModelCode, getConfiguratorModelTreeStructure])

    /*
     * UseEffect to set initial value
     */
    useEffect(() => {
        if (props.value) {
            const path = formatPath(props.value)
            setChecked(path)
        }
    }, [props.value])

    const onCheck = (checked: string[], targetNode: Node) => {
        // For single selection, we need to ensure the clicked node is always selected
        const newValue = targetNode.value

        // Always set the clicked node as selected (ignore the checked array from CheckboxTree)
        const newChecked = [newValue]

        setChecked(newChecked)
        props.onChange(newValue)

        // Force re-render to ensure state is correct
        setTimeout(() => {
            setChecked([newValue])
        }, 1)
    }

    const onExpand = (expanded) => {
        setExpanded(expanded)
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ width: '100%', marginBottom: '8px' }}>
                <InputLabel
                    sx={{
                        fontSize: '0.875rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                        display: 'block',
                        width: '100%',
                        marginBottom: '0px'
                    }}
                >
                    {translate('manager.resources.model_manager.dependency_path_label', {
                        _: 'Path'
                    })}
                </InputLabel>
            </Box>

            <Box
                sx={{
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    padding: '12px',
                    minHeight: '200px',
                    backgroundColor: '#ffffff',
                    width: '100%',
                    clear: 'both',
                    display: 'block',
                    '& .react-checkbox-tree > ol > li': {
                        marginTop: '32px'
                    },
                    '& .react-checkbox-tree ol': {
                        margin: 0,
                        listStyleType: 'none',
                        overflow: 'scroll'
                    }
                }}
            >
                {isTreeStructureLoading ? (
                    <Typography>Loading tree structure...</Typography>
                ) : modelState.currentModelCode && parsedConfiguratorModelTreeStructure?.length ? (
                    <CheckboxTree
                        showNodeIcon={false}
                        showExpandAll={true}
                        noCascade={true}
                        checkModel="all"
                        nodes={parsedConfiguratorModelTreeStructure}
                        checked={checked}
                        expanded={expanded}
                        onCheck={(checked, node) => onCheck(checked, node)}
                        onExpand={onExpand}
                    />
                ) : (
                    <Alert severity="warning">
                        {translate('manager.resources.model_manager.dependency_path_no_tree', {
                            _: 'No tree structure available for the current model'
                        })}
                    </Alert>
                )}
            </Box>

            <Box sx={{ mt: 2 }}>
                <Divider />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    <strong>
                        {translate('manager.resources.rule.actions_tab.path_tree.path_hint', {
                            _: 'Selected Path'
                        })}
                        :
                    </strong>
                    {checked[checked.length - 1] || 'None selected'}
                </Typography>
            </Box>
        </Box>
    )
}

export default DependencyPathSelector
